import type { NoteRow } from '@/core/models/notes.models';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { EmptyRow } from '@/features/EmptyRow';
import './NotesGrid.scss';

/** Saved list notes of one type (related parties, advances, gift aid, ...) with edit and delete actions. */
export function NotesGrid({
  dataSource,
  nameHeader = 'Party Name',
  detailHeader = 'Relationship',
  disabled = false,
  emptyTitle,
  emptyHint,
  onEdit,
  onRemove,
}: {
  dataSource: NoteRow[];
  nameHeader?: string;
  detailHeader?: string;
  /** Disables Delete only. */
  disabled?: boolean;
  /** Shown in place of the rows when the list is empty; leave unset while the list is still loading. */
  emptyTitle?: string;
  emptyHint?: string;
  onEdit: (note: NoteRow) => void;
  onRemove: (note: NoteRow) => void;
}) {
  const columns: DataTableColumn<NoteRow>[] = [
    {
      id: 'name',
      header: nameHeader,
      cell: (element) => (
        <div className="d-flex">
          <div className="onelineclamp">{element.name}</div>
        </div>
      ),
    },
    {
      id: 'relationship',
      header: detailHeader,
      cell: (element) => (
        <div className="d-flex">
          <div className="onelineclamp">{element.detail}</div>
        </div>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      headerClassName: 'w-100',
      cell: (element) => (
        <div className="d-flex align-items-center justify-space-between">
          <div className="d-flex align-items-center grid-actions">
            <Button title="Edit" onClick={() => onEdit(element)}>
              <Icon name="edit" outlined />
            </Button>
            <Button title="Delete" disabled={disabled} onClick={() => onRemove(element)}>
              <Icon name="delete_outline" outlined />
            </Button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="notes-grid">
      <div className="mat-table-wrapper">
        <DataTable
          stickyHeader
          columns={columns}
          rows={dataSource}
          rowKey={(row) => row.id}
          noDataMessage={emptyTitle ? <EmptyRow icon="inbox" title={emptyTitle} hint={emptyHint} /> : undefined}
        />
      </div>
    </div>
  );
}
